import { Button } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneWithControlAndPrefixInput, SelectWithControl, TextInputWithControl } from 'src/components';
import { useAllAgenciesAsSelectData, useRolesMapperSelect } from 'src/hooks';
import { UserSimpleInfo } from 'src/types';
import { useUpdateUserForm } from './hooks';
import { UpdateUserInputs } from './users.types';

type Props = {
  testID?: string;
  user: UserSimpleInfo;
  updateUserError: Error | null;
  onSubmit: (data: UpdateUserInputs) => void;
  onTouched: (isDirty: boolean) => void;
};

export const EditUserForm: React.FC<Props> = ({
  user,
  updateUserError,
  onTouched,
  onSubmit,
  testID = 'EditUserForm',
}) => {
  const { handleSubmit, errors, isDirty, control } = useUpdateUserForm({ initialValues: user });

  const { rolesMapperSelect } = useRolesMapperSelect();

  const { agenciesData } = useAllAgenciesAsSelectData();

  const { t } = useTranslation();

  useEffect(() => {
    onTouched(isDirty);
  }, [isDirty, onTouched]);
  return (
    <div className="w-2/3 m-auto">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <TextInputWithControl
              control={control}
              name="firstName"
              placeholder={t('dashboard-user-create.form-first-name')}
              error={errors.firstName}
              autocomplete="off"
              showUpLabel
            />
          </div>
          <div>
            <TextInputWithControl
              control={control}
              name="lastName"
              placeholder={t('dashboard-user-create.form-last-name')}
              error={errors.lastName}
              autocomplete="off"
              showUpLabel
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <PhoneWithControlAndPrefixInput
              control={control}
              name="tel"
              placeholder={t('dashboard-user-create.form-tel')}
              error={errors?.tel}
              showUpLabel
              copyNumbersOnly={false}
            />
          </div>

          <div>
            <TextInputWithControl
              control={control}
              name="email"
              placeholder={t('dashboard-user-create.form-email')}
              error={errors.email}
              showUpLabel
              type="email"
              autocomplete="off"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <SelectWithControl
              control={control}
              name="role"
              data={rolesMapperSelect}
              placeholder={t('dashboard-user-update.form-role')}
              error={errors.role}
              showUpLabel
            />
          </div>
          <div>
            <SelectWithControl
              control={control}
              name="status"
              data={[
                { label: t('ENDABLED'), value: 'ENDABLED' },
                { label: t('DISABLED'), value: 'DISABLED' },
              ]}
              placeholder={t('dashboard-user-update.form-status')}
              error={errors.status}
              showUpLabel
            />
          </div>

          <div>
            <SelectWithControl
              control={control}
              name="agencies"
              data={agenciesData}
              placeholder={t('dashboard-user-update.form-agencies')}
              error={errors.agencies}
              showUpLabel
              isMulti
            />
          </div>
        </div>

        <div className="mt-4">
          {!!updateUserError && <div className="text-red-600 text-center">{t(updateUserError.message)}</div>}
          <div className="flex justify-end w-full">
            <Button variant="contained" color="primary" type="submit">
              {t('dashboard-user-create.form-save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
